import React from "react";
import {Box, NumberInput, Text} from "@mantine/core";
import {IPPReliefStep, IProtocolStepStatus} from "../../../types/protocol";
import {IPPReliefStepDefinition} from "../../../types/protocolDefinitions";
import stepClasses from "../StepView.module.css";
import {DeepPartial} from "@/utils/types/DeepPartial";
import classes from "./PPReliefStepView.module.css";
import cx from "clsx";
import dayjs from "dayjs";
import {TWorkflow} from "../StepView";
import {showErrorNotification} from "@/common/notifications.tsx";

type PPReliefStep = IPPReliefStep | IPPReliefStepDefinition;

export interface PPReliefStepViewProps {
    step: PPReliefStep;
    timezone?: string;
    onChange: (updates: DeepPartial<PPReliefStep>) => void;
    workflow?: TWorkflow;
}

interface ReliefLevel {
    score: number;
    label: string;
    color: string;
}

const RELIEF_LEVELS: ReliefLevel[] = [
    {score: 0, label: "No Relief", color: "rgb(220 38 38)"},
    {score: 10, label: "Low Relief", color: "rgb(239 68 68)"},
    {score: 20, label: "Low Relief", color: "rgb(234 88 12)"},
    {score: 30, label: "Low Relief", color: "rgb(249 115 22)"},
    {score: 40, label: "Moderate Relief", color: "rgb(251 146 60)"},
    {score: 50, label: "Moderate Relief", color: "rgb(234 179 8)"},
    {score: 60, label: "Moderate Relief", color: "rgb(250 204 21)"},
    {score: 70, label: "High Relief", color: "rgb(163 230 53)"},
    {score: 80, label: "High Relief", color: "rgb(132 204 22)"},
    {score: 90, label: "High Relief", color: "rgb(34 197 94)"},
    {score: 100, label: "Complete Relief", color: "rgb(22 163 74)"},
];

export const PPReliefStepView: React.FC<PPReliefStepViewProps> = ({
    step,
    timezone,
    onChange,
    workflow = "session",
}) => {
    const isPlanningMode = workflow === "planning";
    const isReadOnly = workflow === "review";
    const currentScore = step.score;
    const roundScore = (score: number) => Math.round(score / 10) * 10;

    const onSelected = (score: number) => {
        switch (workflow) {
            case "session":
                if (score >= 0 && score <= 100)
                    onChange({
                        score: score,
                        startTime: dayjs().tz(timezone!, false),
                        endTime: dayjs().tz(timezone!, false),
                        status: IProtocolStepStatus.COMPLETED
                    })
                else {
                    const message = `${score} is invalid, value must be between 0 <= score <= 100`
                    console.log(message)
                    showErrorNotification({
                        title: "Invalid Score",
                        message
                    })
                }
                break;
            case "review_edit":
                // in review edit mode we only want to update the score not start_time or status
                onChange({score: score});
                break;
            default:
                break;
        }
    }

    const getReliefLabel = (score: number | undefined): string => {
        if (score === undefined) return "";
        const roundedScore = roundScore(score)
        const level = RELIEF_LEVELS.find(p => p.score === roundedScore);
        return level?.label ?? "";
    };

    const renderReliefBoxes = () => {
        const roundedScore = currentScore !== undefined ? roundScore(currentScore) : undefined
        return RELIEF_LEVELS.map((level) => {
            const isSelected = roundedScore !== undefined ? roundedScore === level.score : false;
            return (
                <Box key={level.score}>
                    <Box
                         onClick={isReadOnly ? undefined : () => onSelected(level.score)}
                         style={{
                             borderColor: isSelected ? level.color : "",
                             cursor: isReadOnly ? "default" : "pointer",
                         }}
                         className={cx(classes.scoreItem, isSelected && classes.selected)}
                    >
                        <Text
                            size="lg"
                            fw={isSelected ? 700 : "bold"}
                            className={classes.scoreLabel}
                        >
                            {level.score}%
                        </Text>
                    </Box>
                    <Box
                        className={classes.colorBar}
                        style={{backgroundColor: level.color}}
                    />
                </Box>
            );
        })
    }

    return (
        <Box style={{display:"grid", grid:"auto /1fr", alignItems:"center", gridColumn:"1 / -1", justifySelf:"center"}}>
            <Box className={stepClasses.stepViewField} style={{justifySelf: "center", width:"100%"}}>
                <Text c={"dimmed"} ta="center">Click a percentage to record the pain relief level. The timestamp is captured when
                    you select.</Text>
            </Box>

            <Box mb={"1em"} className={stepClasses.stepViewField}
                 style={{justifySelf: "center", gridColumn: "span 4"}}>
                <Text
                    size="lg"
                    fw={600}
                    ta="center"
                    mb="xs"
                    mih={"2em"}
                    className={classes.labelRow}
                >
                    {getReliefLabel(currentScore)}
                </Text>

                <Box className={classes.scoreRow}>
                    <Box className={classes.reliefTextHeader}><Text className={cx(classes.reliefText)}>No Relief</Text></Box>
                    <Box className={classes.reliefTextHeader} style={{gridColumn: "span 3"}}><Text
                        className={cx(classes.reliefText)}>Low Relief</Text></Box>
                    <Box className={classes.reliefTextHeader} style={{gridColumn: "span 3"}}><Text
                        className={cx(classes.reliefText)}>Moderate Relief</Text></Box>
                    <Box className={classes.reliefTextHeader} style={{gridColumn: "span 3"}}><Text
                        className={cx(classes.reliefText)}>High Relief</Text></Box>
                    <Box className={classes.reliefTextHeader}><Text
                        className={cx(classes.reliefText)}>Complete Relief</Text></Box>
                    {renderReliefBoxes()}
                </Box>
                <Box style={{display: "grid", grid: "auto / auto", justifyItems: "center"}}>
                    <NumberInput disabled={isPlanningMode || isReadOnly}
                                 label="Pain Relief Percentage"
                                 value={currentScore}
                                 onBlur={(ev) => onSelected(parseInt(ev.currentTarget.value))}
                                 min={0}
                                 max={100}
                                 step={10}
                                 clampBehavior="strict"
                                 onChange={(ev) => {
                                     const value = typeof ev === "string" ? parseInt(ev) : ev
                                     onSelected(value)
                                 }
                                 }
                    />
                </Box>
            </Box>
        </Box>
    );
};